import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { COMMAND_STYLES, applyStyleColors, type CommandStyleId, type CommandStyle, type CommandStyleCopy } from "@/lib/command-styles";

interface CommandStyleContextValue {
  style: CommandStyle;
  styleId: CommandStyleId;
  copy: CommandStyleCopy;
  setStyle: (id: CommandStyleId) => void;
}

const CommandStyleContext = createContext<CommandStyleContextValue | null>(null);

export function CommandStyleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [styleId, setStyleId] = useState<CommandStyleId>(() => {
    return (localStorage.getItem("ho-command-style") as CommandStyleId) || "operator";
  });

  const style = COMMAND_STYLES[styleId];

  // Load from Supabase on auth
  useEffect(() => {
    if (!user) return;
    supabase
      .from("user_settings")
      .select("command_style")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.command_style) {
          const id = data.command_style as CommandStyleId;
          setStyleId(id);
          localStorage.setItem("ho-command-style", id);
        }
      });
  }, [user?.id]);

  // Apply colors whenever style changes — always applies, clears stale brand kit overrides
  useEffect(() => {
    document.documentElement.style.transition = "background-color 0.3s, color 0.3s";
    applyStyleColors(style.colors);
    setTimeout(() => {
      document.documentElement.style.transition = "";
    }, 300);
  }, [styleId]);

  const setStyle = (id: CommandStyleId) => {
    setStyleId(id);
    localStorage.setItem("ho-command-style", id);
    // Clear any custom color overrides so the style colors take effect
    localStorage.removeItem("ho-accent-color");
    localStorage.removeItem("ho-brand-bg");
    localStorage.removeItem("ho-brand-card");
    localStorage.removeItem("ho-brand-sidebar");
    localStorage.removeItem("ho-brand-border");
    applyStyleColors(COMMAND_STYLES[id].colors);

    if (user) {
      supabase
        .from("user_settings")
        .upsert(
          { user_id: user.id, command_style: id, updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        )
        .then(() => {});
    }
  };

  return (
    <CommandStyleContext.Provider value={{ style, styleId, copy: style.copy, setStyle }}>
      {children}
    </CommandStyleContext.Provider>
  );
}

export function useCommandStyle() {
  const ctx = useContext(CommandStyleContext);
  if (!ctx) {
    // Fallback for components outside the provider (like Landing page)
    const fallbackStyle = COMMAND_STYLES.operator;
    return {
      style: fallbackStyle,
      styleId: "operator" as CommandStyleId,
      copy: fallbackStyle.copy,
      setStyle: () => {},
    };
  }
  return ctx;
}
